/**
 * TokenCard Component - Token management card
 */

import React from 'react'
import { Card, Badge } from '../ui'
import { Copy, Check, X, Clock, Eye, User, Calendar } from 'lucide-react'

const TokenCard = ({
  token,
  onCopy,
  onRevoke,
  onView,
  copied = false,
  className = '',
  ...props
}) => {
  const isActive = token.status === 'active'
  const isExpired = new Date(token.expires) < new Date()
  const statusVariant = isExpired ? 'error' : isActive ? 'success' : 'default'

  const statusLabel = isExpired ? 'Expired' : isActive ? 'Active' : 'Revoked'

  return (
    <Card className={`p-4 ${className}`} {...props}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <User size={14} className="text-muted" />
            <span className="text-sm font-semibold text-ink truncate">
              {token.recipient || token.investor_email}
            </span>
          </div>
          <p className="text-xs text-muted font-mono mt-1 truncate">{token.id}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-muted">
            <span className="flex items-center gap-1">
              <Calendar size={11} />
              Created: {token.created}
            </span>
            {token.expires && (
              <span className="flex items-center gap-1">
                <Clock size={11} />
                Expires: {token.expires}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Badge variant={statusVariant} size="sm">
            {statusLabel}
          </Badge>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-neutral-100 flex items-center justify-between">
        <span className="text-xs text-muted">Scope: {token.scope || 'View Summary'}</span>
        <div className="flex items-center gap-1.5">
          {isActive && !isExpired && (
            <>
              <button
                onClick={() => onView?.(token)}
                className="p-1.5 text-neutral-400 hover:text-brand rounded-lg hover:bg-neutral-100 transition-colors"
                title="View token details"
              >
                <Eye size={14} />
              </button>
              <button
                onClick={() => onCopy?.(token.id)}
                className="p-1.5 text-neutral-400 hover:text-brand rounded-lg hover:bg-neutral-100 transition-colors"
                title="Copy token"
              >
                {copied ? <Check size={14} className="text-success" /> : <Copy size={14} />}
              </button>
              <button
                onClick={() => onRevoke?.(token.id)}
                className="p-1.5 text-neutral-400 hover:text-error rounded-lg hover:bg-neutral-100 transition-colors"
                title="Revoke token"
              >
                <X size={14} />
              </button>
            </>
          )}
        </div>
      </div>
    </Card>
  )
}

TokenCard.displayName = 'TokenCard'

export default TokenCard